import { type QueryHookOptions } from '@apollo/client';
import { number, object, string } from 'yup';

import type {
  AiOriginalImageQuery,
  AiOriginalImageQueryVariables,
  User,
} from 'src/shared/graphql/generated';
import {
  useAiOriginalImageAnonymousQuery,
  useAiOriginalImageQuery,
  useProfileQuery,
} from 'src/shared/graphql/generated';
import { useParsedParams } from 'src/shared/hooks';
import { parseParams } from 'src/shared/hooks/useParsedParams';
import { union } from 'src/shared/validationSchemas';

import { sampleImages } from '../components/Home/SampleImages/sampleImages';

const sampleImageIds = sampleImages.map(({ sampleId }) => sampleId);

export type Options = Omit<
  QueryHookOptions<AiOriginalImageQuery, any>,
  'variables'
>;

export type OriginalImageVariables = Omit<AiOriginalImageQueryVariables, 'id'>;

interface UseActiveOriginalImageOptions {
  variables?: OriginalImageVariables;
  options?: Options;
  originalImageId?: number;
}

const parseOriginalImageParams = (params: Record<string, unknown>) =>
  parseParams(
    params,
    object({
      aiOriginalImageId: union([
        number().required(),
        string().oneOf(sampleImageIds).required(),
      ]),
    }),
  );

const useActiveOriginalImageParams = () =>
  useParsedParams(
    object({
      aiOriginalImageId: union([
        number().required(),
        string().oneOf(sampleImageIds).required(),
      ]),
    }),
  );

/**
 * Queries for the original image with the ID specified in the URL (i.e:
 * `/original/:aiOriginalImageId`). If the `aiOriginalImageId` cannot be parsed
 * from the URL, `image` will be `undefined` and the parsing error will be
 * available as `error`
 */
const useActiveOriginalImage = ({
  variables,
  options,
  originalImageId,
}: UseActiveOriginalImageOptions = {}) => {
  const params = useActiveOriginalImageParams();

  const { aiOriginalImageId } = params;
  const isSample = typeof aiOriginalImageId === 'string';

  const failed = '__error' in params && !originalImageId;
  const id = String(originalImageId ?? params.aiOriginalImageId);

  const { data: profileData, error: profileError } = useProfileQuery({
    errorPolicy: 'all',
  });
  const profileLoading = !profileData && !profileError;
  const currentUser = profileData?.profile?.user as User;
  const loggedIn = !!currentUser;
  const skipAll = !id || profileLoading || failed || isSample;

  const {
    data: authedData,
    previousData: authedPreviousData,
    ...authedResult
  } = useAiOriginalImageQuery({
    ssr: false,
    skip: skipAll || !loggedIn,
    variables: { ...variables, id },
    errorPolicy: 'all',
    ...options,
  });

  // There is a time delay when assigning an anonymously uploaded image to a user when they authenticate.
  // If authedData?.aiOriginalImage is null, we rely on the anonymous query.
  // Skip the anonymous query if we are logged in and have a result, or are loading a result.
  const shouldUseAuthenticatedQuery =
    loggedIn &&
    (!authedResult.called ||
      authedResult.loading ||
      !!authedData?.aiOriginalImage);

  const {
    data: anonData,
    previousData: anonPreviousData,
    ...anonResult
  } = useAiOriginalImageAnonymousQuery({
    ssr: false,
    skip: skipAll || shouldUseAuthenticatedQuery,
    variables: { ...variables, id },
    errorPolicy: 'all',
    ...options,
  });

  const data = shouldUseAuthenticatedQuery
    ? authedData
    : {
        aiOriginalImage: anonData?.aiOriginalImageAnonymous,
      };

  const previousData = shouldUseAuthenticatedQuery
    ? authedPreviousData
    : {
        aiOriginalImage: anonPreviousData?.aiOriginalImageAnonymous,
      };

  const result = shouldUseAuthenticatedQuery ? authedResult : anonResult;

  if (!failed) {
    if (isSample) {
      const sample = sampleImages.find(
        image => image.sampleId === aiOriginalImageId,
      );

      return {
        ...result,
        image: sample ?? null,
        loading: false,
        previousImage: null,
        error: undefined,
      };
    }

    return {
      image: data ? data.aiOriginalImage : undefined,
      previousImage: previousData?.aiOriginalImage,
      ...result,
    };
  }

  const { __error: paramsError } = params;

  return {
    ...result,
    image: null,
    loading: false,
    previousImage: null,
    error: paramsError,
  };
};

export {
  useActiveOriginalImage,
  parseOriginalImageParams,
  useActiveOriginalImageParams,
};
